import {
  call, put, select, takeLatest, all
} from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';
import actions from 'react/api/actions';
import MdlpService from 'react/services/mdlp.service';


const {
  auth: {
    actions: {
      login, request, getIdentityProvidersListSucceeded, getIdentityProvidersListRequest
    }
  },
  configuration: {
    actions: { setConfiguration }
  },
  features: {
    actions: { setFeature }
  },
  dashboardAnalytics: {
    actions: { setAnalyticsToken }
  },
  user: {
    actions: { updateUser }
  }
} = actions;

const createConfig = (method = 'POST', payload = {}) => {
  const config = {
    method,
    credentials: 'include',
    body: payload,
    headers: {
      Accept: 'application/json',
      Pragma: 'no-cache',
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      'Cache-Control': 'no-cache',
      'Greenmile-Module': 'LIVE',
      'Greenmile-Version': App.Version.GMLVersionNumber,
      'Greenmile-Build': App.Version.GMLBuildNumber
    }
  };

  return config;
};

const validateFetch = (response) => {
  const result = {};
  const genericError = {
    status: response.status || 500,
    responseText: response.statusText || response.message,
    resourceKey: 'auth.genericError'
  };
  return response
    .json()
    .then((serverError) => {
      result.error = serverError;
      return result;
    })
    .catch(() => {
      result.error = genericError;
      return result;
    });
};

function loginFetch(payload) {
  const body = Object.keys(payload)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`)
    .join('&');

  return fetch('/login', createConfig('POST', body)).then((response) => {
    if (response.status >= 400) {
      return validateFetch(response);
    }
    return response.json();
  });
}

function* authLoginSaga() {
  const { auth } = yield select();
  const payload = auth.get('user').toJS();
  if (payload.j_username && payload.j_password) {
    const response = yield call(loginFetch, payload);
    const { error, analyticsToken } = response;
    if (error) {
      yield put({
        type: actions.auth.actions.loginFailure,
        payload: { ...error, showRecaptcha: true }
      });
    } else {
      yield put({ type: actions.auth.actions.request });
      if (analyticsToken) {
        yield put({ type: setAnalyticsToken, payload: analyticsToken });
      }
    }
  } else {
    yield put(actions.auth.actionCreators.showErrorsAtLogin(payload));
  }
}

function prepareLive(payload) {
  Monet.Session.ApplicationLoad = payload;
  Monet.Session.User = payload.loggedUser;
  Monet.Session.isLogged = true;
  Monet.Session.User = Monet.Session.User || {};
  if (!Monet.Session.User.unitSystem) {
    Monet.Session.User.unitSystem = 'NON_METRIC';
  }
  // TODO: Dispatching action to save rules.
  Monet.Security.setScreensEnabledByMenu(payload.menus);
  Greenmile.LoadAllSessionInfo.startInitialFunctions();
  return payload;
}

function fetchApplicationLoad() {
  const { search } = window.location;
  return fetch(`/ApplicationLoad/${search}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    redirect: 'follow'
  });
}

function configureTimeout() {
  const { login } = Monet.Session.User;

  fetch(`/SecurityPolicy/ByLogin?login=${login}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    redirect: 'follow'
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.length) {
        const minutes = parseInt(data[0].rule, 10);
        Greenmile.LoadAllSessionInfo.initTimeout(minutes);
      }
    });
}

function requestUserPreferences(loggedUser) {
  const tenant = Greenmile.Utils.getTenant();

  const url = `/live-metadata/UserScreenPreference/alias/${tenant}/${loggedUser.id}`;

  return fetch(url, {
    method: 'GET'
  });
}

function* mergeUserPreferencesWithAppData(actualPayload) {
  const response = yield call(
    requestUserPreferences,
    actualPayload.loggedUser
  );

  if (response.status === 200) {
    const genericUserScreenPreferences = yield call(() => response.json() || []);

    return Object.assign(
      actualPayload,
      {
        genericUserScreenPreferences
      }
    );
  }

  return actualPayload;
}

function fetchIdentityProviderList() {
  return fetch('/auth/idp/discovery', {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    redirect: 'follow'
  });
}

function* getIdentityProvidersList() {
  const response = yield call(fetchIdentityProviderList);
  if (response.status === 200) {
    const payload = yield call(() => response.json());
    yield put({ type: getIdentityProvidersListSucceeded, payload });
  }
}

function* redirectToLogin() {
  yield put(replace('/login'));
}

function* redirectAppAfterLogin() {
  const { location: { hash } } = window;
  const { tabs } = yield select();
  const route = tabs.get('active').get('pathname');
  const isRecoverPassword = '/RecoverPassword'

  if (route && !hash.includes(route) && route !== isRecoverPassword) {
    yield put(push(route));
  } else if (!route || route === isRecoverPassword) {
    yield put(replace('/RouteMonitor'));
  }
}

function configureMdlpService() {
  const mdlpService = new MdlpService();

  mdlpService.connect();
}

function* updateAppFeaturesAndConfigStates(payload) {
  yield put({ type: setFeature, payload });

  const { features } = yield select();
  const configuration = { ...payload, featureStates: features };

  yield put({
    type: setConfiguration,
    payload: {
      configuration
    }
  });
  yield put({ type: updateUser, payload: configuration.loggedUser });
}

function removeTokenFromUrl() {
  const { location: { search }, history } = window;
  if (search && history && history.replaceState) {
    history.replaceState(null, null, '/');
  }
}

function getLiveMetadataStatus({ featureStates }) {
  return featureStates?.some((feature) => feature.name === 'LIVE_METADATA' && feature.enabled);
}

function* getParsedPayload(payload) {
  const liveMetadataIsOn = getLiveMetadataStatus(payload);

  if (liveMetadataIsOn) {
    const mergedPayload = yield mergeUserPreferencesWithAppData(payload);

    return mergedPayload;
  }

  return payload;
}

function* authRequestSaga() {
  try {
    const { location: { search } } = window;
    if (!search.includes('?error')) {
      const response = yield call(fetchApplicationLoad);
      yield call(removeTokenFromUrl);
      if (response.status < 400) {
        const responseJson = yield call(() => response.json());

        const payload = yield call(getParsedPayload, responseJson);
        const tenant = Greenmile.Utils.getTenant(true);
        const isTenantIp = Greenmile.Utils.checkIfTenantIsIp();

        yield prepareLive(payload);
        yield updateAppFeaturesAndConfigStates(payload);
        yield redirectAppAfterLogin();

        configureTimeout();
        configureMdlpService();

        const pendoIsEnabled = Monet.Session.ApplicationLoad.featureStates?.some(
          (feature) =>
          feature.name === "GM_PENDO"
          && feature.enabled
        );

        if (pendoIsEnabled && (tenant !== 'localhost' && isTenantIp === false)) {
          App.initializePendo(payload);
        }

      } else {
        yield put({ type: actions.auth.actions.loginFailure, payload: [] });
        yield call(redirectToLogin);
      }
    } else {
      yield put({
        type: actions.auth.actions.loginFailure,
        payload: {
          resourceKey: decodeURIComponent(search.replace('?error=', '')).replace(/\+/g, ' ')
        }
      });
      yield call(removeTokenFromUrl);
      yield call(redirectToLogin);
    }
  } catch (err) {
    console.log('Err', err);
  }
}

function* watchAuthLogin() {
  yield takeLatest(login, authLoginSaga);
}

function* watchAuthRequest() {
  yield takeLatest(request, authRequestSaga);
}

function* watchIdentityProvidersListRequest() {
  yield takeLatest(getIdentityProvidersListRequest, getIdentityProvidersList);
}

export default function* () {
  yield all([
    watchAuthLogin(),
    watchAuthRequest(),
    watchIdentityProvidersListRequest()
  ]);
}
